/* 
Generated code: do not modify this file as it will be overwritten
Template: ImmutableJSRecord.cst

Known Issues/TODO:
- Using *Props type as Record constructor parameter is insufficient.
If a property needs to be a Record type, it should already be constructed.
- Rename Record fromJS factory method to fromJSON
- Create parseJSON method that translates JSON to Props



 */

/* eslint-disable import/order */

import { createSelector } from '@ngrx/store';
import { List, Map, Record, Seq, Set } from 'immutable';
import { AppState } from 'src/store/appstate';
import { getFromOrThrow, propValueOrDefault, tryOrDefault } from 'src/util/values';

export interface LocationHazardAttachmentProps {
  id: string;
  locationHazardId: string;
  fileName: string;
  fileSizeInBytes: number | null;
  contentType: string | null;
  companyId: string;
  dateLastUpdated: Date;
  dateCreated: Date;
  dateDeleted: Date | null;
  lastUpdatedById: string | null;
}

export class LocationHazardAttachmentRecord extends Record<LocationHazardAttachmentProps>({
  id: null as any,
  locationHazardId: null as any,
  fileName: null as any,
  fileSizeInBytes: null as any,
  contentType: null as any,
  companyId: null as any,
  dateLastUpdated: null as any,
  dateCreated: null as any,
  dateDeleted: null as any,
  lastUpdatedById: null as any,
}) {
  public static fromJS(json: {
    id: string;
    locationHazardId: string;
    fileName: string;
    fileSizeInBytes: number | null;
    contentType: string | null;
    companyId: string;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): LocationHazardAttachmentRecord {
    if (json == null) {
      throw new Error('LocationHazardAttachmentRecord.fromJS: parameter cannot be null');
    }
    return new LocationHazardAttachmentRecord({
      id: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
        'LocationHazardAttachmentJson.id',
      ),
      locationHazardId: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'locationHazardId', '00000000-0000-0000-0000-000000000000'),
        'LocationHazardAttachmentJson.locationHazardId',
      ),
      fileName: getFromOrThrow(json, (j) => j.fileName, 'LocationHazardAttachmentJson.fileName'),
      fileSizeInBytes: json.fileSizeInBytes,
      contentType: json.contentType,
      companyId: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'companyId', '00000000-0000-0000-0000-000000000000'),
        'LocationHazardAttachmentJson.companyId',
      ),
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });
  }

  public static fromJSUnsafe(json: {
    id: string;
    locationHazardId: string;
    fileName: string;
    fileSizeInBytes: number | null;
    contentType: string | null;
    companyId: string;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): [LocationHazardAttachmentRecord, Error[]] {
    if (json == null) {
      throw new Error('LocationHazardAttachmentRecord.fromJSUnsafe: parameter cannot be null');
    }
    const errors: Error[] = [];

    const record = new LocationHazardAttachmentRecord({
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      id: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
            'LocationHazardAttachmentJson.id',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      locationHazardId: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) =>
              propValueOrDefault(j, 'locationHazardId', '00000000-0000-0000-0000-000000000000'),
            'LocationHazardAttachmentJson.locationHazardId',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      fileName: tryOrDefault(
        () => getFromOrThrow(json, (j) => j.fileName, 'LocationHazardAttachmentJson.fileName'),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      fileSizeInBytes: tryOrDefault(
        () => json.fileSizeInBytes,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      contentType: tryOrDefault(
        () => json.contentType,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      companyId: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'companyId', '00000000-0000-0000-0000-000000000000'),
            'LocationHazardAttachmentJson.companyId',
          ),
        (err) => errors.push(err),
      )!,
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });

    return [record, errors];
  }

  constructor(props: LocationHazardAttachmentProps) {
    super(props);
    if (props == null) {
      throw new Error('LocationHazardAttachmentRecord constructor parameter cannot be null');
    }
  }
}

export abstract class LocationHazardAttachmentViewModelBase
  implements LocationHazardAttachmentProps
{
  protected readonly _props: LocationHazardAttachmentProps;
  private _json?: LocationHazardAttachmentProps;

  get id(): string {
    return this._props.id;
  }
  get locationHazardId(): string {
    return this._props.locationHazardId;
  }
  get fileName(): string {
    return this._props.fileName;
  }
  get fileSizeInBytes(): number | null {
    return this._props.fileSizeInBytes;
  }
  get contentType(): string | null {
    return this._props.contentType;
  }
  get companyId(): string {
    return this._props.companyId;
  }

  get dateCreated(): Date {
    return this._props.dateCreated;
  }
  get dateLastUpdated(): Date {
    return this._props.dateLastUpdated;
  }
  get dateDeleted(): Date | null {
    return this._props.dateDeleted;
  }
  get lastUpdatedById(): string | null {
    return this._props.lastUpdatedById;
  }

  constructor(props: LocationHazardAttachmentProps) {
    this._props = props;
  }

  public toJS() {
    return this.toJSON();
  }

  public toJSON() {
    if (this._json == null) {
      this._json = {
        id: this._props.id,
        locationHazardId: this._props.locationHazardId,
        fileName: this._props.fileName,
        fileSizeInBytes: this._props.fileSizeInBytes,
        contentType: this._props.contentType,
        companyId: this._props.companyId,
        dateLastUpdated: this._props.dateLastUpdated,
        dateCreated: this._props.dateCreated,
        dateDeleted: this._props.dateDeleted,
        lastUpdatedById: this._props.lastUpdatedById,
      };
    }
    return this._json;
  }
}

//#region Selectors

export const selectAllLocationHazardAttachmentEntities = (state: AppState) =>
  state.entityStores.locationHazardAttachment.entities;

export const selectLocationHazardAttachmentById = (id: string) => (state: AppState) =>
  state.entityStores.locationHazardAttachment.entities.get(id);

export const selectLocationHazardAttachmentMetadataById = (id: string) => (state: AppState) =>
  state.entityStores.locationHazardAttachment.meta.get(id);

export const selectLocationHazardAttachmentAndMetadataById = (id: string) =>
  createSelector(
    selectLocationHazardAttachmentById(id),
    selectLocationHazardAttachmentMetadataById(id),
    (locationHazardAttachment, meta) => ({ locationHazardAttachment, meta }),
  );

export function selectLocationHazardAttachmentEntitiesByIds(ids: string[]) {
  return createSelector(
    selectAllLocationHazardAttachmentEntities,

    (entities) => {
      // TODO: Once we're on TS 3.5+, I don't think we'll need use type assertions
      const entries = ids.map((id) => [id, entities.get(id)]) as unknown as Iterable<
        [string, LocationHazardAttachmentRecord | undefined]
      >;
      return Map(entries);
    },
  );
}

export function selectLocationHazardAttachmentEntitiesByLocationHazardId(locationHazardId: string) {
  return createSelector(selectAllLocationHazardAttachmentEntities, (entities) =>
    entities.filter((e) => e.locationHazardId === locationHazardId).valueSeq(),
  );
}

export function selectLocationHazardAttachmentEntitiesByFileName(fileName: string) {
  return createSelector(selectAllLocationHazardAttachmentEntities, (entities) =>
    entities.filter((e) => e.fileName === fileName).valueSeq(),
  );
}

export function selectLocationHazardAttachmentEntitiesByFileSizeInBytes(fileSizeInBytes: number) {
  return createSelector(selectAllLocationHazardAttachmentEntities, (entities) =>
    entities.filter((e) => e.fileSizeInBytes === fileSizeInBytes).valueSeq(),
  );
}

export function selectLocationHazardAttachmentEntitiesByContentType(contentType: string) {
  return createSelector(selectAllLocationHazardAttachmentEntities, (entities) =>
    entities.filter((e) => e.contentType === contentType).valueSeq(),
  );
}

export function selectLocationHazardAttachmentEntitiesByCompanyId(companyId: string) {
  return createSelector(selectAllLocationHazardAttachmentEntities, (entities) =>
    entities.filter((e) => e.companyId === companyId).valueSeq(),
  );
}
//#endregion Selectors
